import { useState } from 'react';
import {
  Control,
  Controller,
  useFormContext,
  UseFormSetValue,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { MdDelete } from 'react-icons/md';
import { DEPOT_IMAGES_URL, HERE_APIKEY, COUNTRY_CODES } from '@constants';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { Textarea } from '../../../tailwindComponents/Textarea';
import { Dropzone } from '../../../tailwindComponents/Dropzone';
import { Autocomplete } from '../../../tailwindComponents/Autocomplete';
import { PhoneNumberInput } from '../../../tailwindComponents/PhoneNumberInput';
import { DepotFormValues } from '../types';

export function General({
  control,
  formState,
  setValue,
}: {
  control: Control<DepotFormValues>;
  formState: UseFormStateReturn<DepotFormValues>;
  setValue: UseFormSetValue<DepotFormValues>;
}) {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState<string | null>(null);
  const { getValues, clearErrors } = useFormContext();

  const images = useWatch({
    control,
    name: 'images',
  });

  const phoneCountryCode = useWatch({
    control,
    name: 'phoneCountryCode',
  });

  const handleFetchLocationSuggestions = (input: string) => {
    fetch(
      `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${input}&at=52.93175,12.77165`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.items) {
          const items = data.items.map(
            (item: {
              id: any;
              address: { label: string; city: string; countryName: string };
            }) => ({
              id: item.id,
              label: item.address.label,
              city: item.address.city,
              country: item.address.countryName,
            })
          );
          setOptions(items);
        }
      })
      .catch((error) => {
        console.error('Error fetching suggestions:', error);
      });
  };

  const handleLocationOptionSelect = async (value: any) => {
    if (value && value.id) {
      setInputValue(value.label);
      setValue('address', value.label);
      setValue('city', value.city);
      setValue('country', value.country);
      clearErrors('address');
      try {
        const response = await fetch(
          `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
        );

        const data = await response.json();

        if (data) {
          const { position } = data;

          if (position) {
            const { lat, lng: lon } = position;

            const location = {
              lat,
              lon,
            };

            setValue('location', location);
            if (data.address?.postalCode) {
              setValue('postcode', data.address.postalCode);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching position information:', error);
      }
    }
  };

  const buildImagePreviewSrc = (image: any) => {
    return `${DEPOT_IMAGES_URL}/${image.imageLocation}/small/${image.remoteName}`;
  };

  return (
    <>
      <Subheader subHeader='Depot Details' />
      <div className='flex space-x-4'>
        <div className='flex flex-col space-y-4 grow'>
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <Input
                label='Name'
                {...field}
                isInvalid={!!formState.errors.name}
                validationMessage={formState.errors.name?.message as string}
              />
            )}
          />
          <Controller
            name='address'
            control={control}
            render={({ field }) => (
              <Autocomplete
                label='Location'
                options={options}
                value={inputValue ?? field.value}
                onInputChange={(newInputValue) => {
                  setInputValue(newInputValue);
                  handleFetchLocationSuggestions(newInputValue);
                }}
                onChange={(value) => {
                  handleLocationOptionSelect(value);
                }}
                isInvalid={!!formState.errors.address}
                validationMessage={formState.errors.address?.message as string}
              />
            )}
          />
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <Textarea label='Description' {...field} optional />
            )}
          />
        </div>
        <div className='flex flex-col space-y-4'>
          {images && images.length > 0 ? (
            <>
              <div className='block text-sm leading-5 font-medium text-gray-900 font-inter'>
                Image
                <span className='text-sm leading-5 font-normal font-inter text-gray-400 ml-2'>
                  Optional
                </span>
              </div>
              <div className='relative'>
                <img
                  src={buildImagePreviewSrc(images[0])}
                  alt={images[0].title || 'Depot image'}
                  className='object-contain max-h-60 max-w-full rounded'
                />
                <button
                  type='button'
                  onClick={() => {
                    const img = images[0];
                    setValue('deletedImagesIds', [
                      ...(getValues('deletedImagesIds') || []),
                      img.id,
                    ]);
                    setValue('images', []);
                  }}
                  aria-label='Delete image'
                  className='absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
                >
                  <MdDelete size={20} />
                </button>
              </div>
            </>
          ) : (
            <Dropzone
              label='Image'
              optional
              onDrop={(acceptedFiles: any) => {
                setValue('depotImages', [acceptedFiles]);
              }}
            />
          )}
        </div>
      </div>
      <Subheader subHeader='Contact Information' />
      <div className='flex space-x-4'>
        <Controller
          name='contactName'
          control={control}
          render={({ field }) => (
            <Input
              label='Contact name'
              {...field}
              isInvalid={!!formState.errors.contactName}
              validationMessage={
                formState.errors.contactName?.message as string
              }
              optional
            />
          )}
        />
        <Controller
          name='contactEmail'
          control={control}
          render={({ field }) => (
            <Input
              label='Contact email'
              {...field}
              isInvalid={!!formState.errors.contactEmail}
              validationMessage={
                formState.errors.contactEmail?.message as string
              }
              optional
            />
          )}
        />
        <Controller
          name='phoneNumber'
          control={control}
          render={({ field }) => (
            <PhoneNumberInput
              label='Phone number'
              {...field}
              isInvalid={!!formState.errors.phoneNumber}
              validationMessage={
                formState.errors.phoneNumber?.message as string
              }
              countryCode={phoneCountryCode}
              onCountryCodeChange={(code: string) =>
                setValue('phoneCountryCode', code)
              }
              optional
            />
          )}
        />
      </div>
      <Subheader subHeader='Facilities' />
      <div className='flex space-x-4'>
        <Controller
          name='loadingDocks'
          control={control}
          render={({ field }) => (
            <Input
              label='Loading docks'
              {...field}
              isInvalid={!!formState.errors.loadingDocks}
              validationMessage={
                formState.errors.loadingDocks?.message as string
              }
              optional
            />
          )}
        />
      </div>
    </>
  );
}
